import { useEffect } from "react";
import NoRolesAssigned from "./components/Access/NoRolesAssigned";
import { useLocation, useNavigate } from "react-router-dom";
import AppEntry from "./components/Layout/AppEntry";
import RoutedContent from "./components/Layout/RoutedContent";
import InactivityHandler from "./components/Layout/InactivityHandler";
import MfaSetupScreen from "./components/Auth/MfaSetupScreen";
import EnrollmentScreen from "./components/Enrollment/EnrollmentScreen";
import GoodFaithEstimatePage from "./pages/GoodFaithEstimatePage";
import UnauthenticatedRouting from "./UnauthenticatedRouting";
import { useCerberus } from "./hooks/useCerberus";
import SessionService from "./services/SessionService";
import { storeIntendedDestination } from "./utils/redirectUtils";
import { markHydrated } from "./appHydration";

function isUrlExcludedFromAuth(url: string) {
  return url.startsWith("/i/");
}

function isAuthRoute(url: string) {
  // /secure-account/<code> carries a single-use code; it must never be stored as the
  // "return here after signing in" destination.
  return (
    url === "/forgot-password" ||
    url === "/reset-password" ||
    url.startsWith("/secure-account/")
  );
}

/**
 * The root route's element: decides which shell the matched route renders inside.
 *
 * This gate is identity-based rather than path-based, so it stays an element branch rather than
 * becoming route structure. When the user is not signed in it short-circuits into the
 * unauthenticated tree and never renders the Outlet, which is what keeps an anonymous visitor to
 * /dashboard on the login page even though /dashboard now matches for everyone.
 */
const RootLayout = () => {
  const { loading, isRegistered, mfaSetupRequired, enrollmentRequired, roles, cerberusUser } = useCerberus();
  const sessionService = SessionService.getInstance();
  const isSignedIn = sessionService.isAuthenticated();
  const location = useLocation();
  const navigate = useNavigate();

  // From here on there is a page on screen, so loaders may block a navigation on their data.
  useEffect(markHydrated, []);

  // Store intended destination when user is not authenticated
  useEffect(() => {
    if (
      !loading &&
      !isSignedIn &&
      !isUrlExcludedFromAuth(location.pathname) &&
      !isAuthRoute(location.pathname)
    ) {
      // Only store if it's not the login page itself
      if (location.pathname !== "/" && location.pathname !== "/login") {
        storeIntendedDestination(location.pathname + location.search);
      }
    }
  }, [loading, isSignedIn, location.pathname, location.search]);

  if (loading) {
    // Still checking authentication status
    return null;
  }

  // Invitation links, the password-reset flow and everything else all resolve inside the
  // unauthenticated tree, so one branch covers them.
  if (!isSignedIn) {
    return <UnauthenticatedRouting />;
  }

  // TeleHeadache Review & Sign comes before registration: eligibility, electronic paperwork, the
  // signed membership and payment, then the /register wizard. Only ever true for a patient on a
  // deployment that has enrollment switched on — the server decides.
  //
  // One flow, not two (2026-10-05): where enrollment applies, the health history that the /register
  // wizard used to collect is the enrollment screen's last step, after payment — so a member who
  // has paid but not finished it stays here too, rather than meeting a second wizard.
  if (enrollmentRequired || (cerberusUser.enrollmentApplies && !isRegistered)) {
    // CONS-5: the one page enrollment lets through. The patient notice offers a Good Faith Estimate
    // before anyone has paid — which is exactly when someone wants one.
    if (location.pathname === "/good-faith-estimate") {
      return <GoodFaithEstimatePage onBack={() => navigate("/")} />;
    }
    return <EnrollmentScreen />;
  }

  // Registration is the one authenticated flow that runs without the app shell.
  if (!isRegistered) {
    return <RoutedContent />;
  }

  // A signed-in account with no role can do nothing: the API refuses it everything but identity and
  // session. It gets the explanation INSTEAD of the shell — the shell used to render around an
  // endless spinner (RoutedContent read "no roles" as "roles not loaded yet"). Ahead of the MFA
  // gate on purpose: the phone-setup screen's calls are refused to a roleless account too.
  if (!roles?.length) {
    return <NoRolesAssigned />;
  }

  // Hard gate: staff / providers / external providers must verify a phone for MFA before
  // the app shell renders. Verifying clears the flag on the next identity refresh.
  if (mfaSetupRequired) {
    return <MfaSetupScreen />;
  }

  return (
    <>
      <InactivityHandler />
      <AppEntry />
    </>
  );
};

export default RootLayout;
